import React, { ReactElement, useState } from 'react'
import {
  Box,
  Card,
  CardMedia,
  Chip,
  LinearProgress,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material'
import ImageIcon from '@mui/icons-material/Image'

import { PlanningCourseV2, CourseV2Progress } from 'src/v3/core/types'
import {
  courseResizedImageSrc,
  onCourseImagePublicFallback,
} from 'src/v3/self-paced/utils/course-thumbnail'
import LifecycleChip from '../../../components/lifecycle/lifecycle-chip'

type CourseListWithProgressProps = {
  courses: PlanningCourseV2[]
  coursesProgress: Record<string, CourseV2Progress>
  activeCourseID?: string
  onSelectCourse: (courseID: string) => void
  isLoading: boolean
  filterType?:
    'all' | 'needs-revision' | 'in-planning' | 'ready-for-review' | 'approved'
  searchQuery?: string
}

let CourseCard = ({
  course,
  progress,
  checked,
  onSelect,
}: {
  course: PlanningCourseV2
  progress: CourseV2Progress | undefined
  checked: boolean
  onSelect: () => void
}) => {
  let theme = useTheme()
  let [imageError, setImageError] = useState(false)
  let thumbnailUrl = course.imageName
    ? courseResizedImageSrc(course.imageName)
    : null

  let dataProgress = progress?.dataCompleteness.percentComplete ?? 0
  let complianceProgress = progress?.complianceStatus.percentApproved ?? 0
  let approvedFields = progress?.complianceStatus.approved ?? 0
  let totalFields = progress
    ? progress.complianceStatus.approved +
      progress.complianceStatus.pending +
      progress.complianceStatus.rejected +
      progress.complianceStatus.notSubmitted
    : 0

  return (
    <Box role="listitem">
      <Card
        variant="outlined"
        component="button"
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: checked ? 'rgba(25, 118, 210, 0.12)' : undefined,
          '&:hover': {
            cursor: 'pointer',
            bgcolor: checked ? 'rgba(25, 118, 210, 0.12)' : 'divider',
          },
          '&:focus': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
          border: 'none',
          textAlign: 'left',
          width: '100%',
        }}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelect()
          }
        }}
        aria-pressed={checked}
        aria-label={`Select ${course.name}`}
        tabIndex={0}
      >
        <Box sx={{ display: 'flex' }}>
          {thumbnailUrl && !imageError ? (
            <CardMedia
              component="img"
              image={thumbnailUrl}
              alt={course.name}
              onError={(e) => {
                if (e.currentTarget.src.includes('/images/')) {
                  onCourseImagePublicFallback(e, course.imageName)
                } else {
                  setImageError(true)
                }
              }}
              sx={{
                width: 120,
                height: 80,
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
          ) : (
            <Box
              sx={{
                width: 120,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paperDark',
                flexShrink: 0,
              }}
            >
              <ImageIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            </Box>
          )}
          <Box sx={{ p: 2, flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 0.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {course.name}
              </Typography>
              <LifecycleChip lifecycleState={course.lifecycleState} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {course.publishedVersion === 1 && (
                <Chip
                  color="warning"
                  size="small"
                  label="Needs Migration"
                  variant={checked ? 'filled' : 'outlined'}
                />
              )}
              {course.publishedVersion === 2 &&
                (() => {
                  let isPublished = course.published === 'published'
                  return (
                    <Chip
                      size="small"
                      label={isPublished ? 'Published' : 'Unpublished'}
                      color={isPublished ? 'success' : undefined}
                      sx={{
                        ...(!isPublished && {
                          color:
                            theme.palette.mode === 'dark'
                              ? 'rgba(0, 0, 0, 0.87)'
                              : undefined,
                          bgcolor: theme.palette.analyticsColors.ceHours,
                        }),
                      }}
                      variant={checked ? 'filled' : 'outlined'}
                    />
                  )
                })()}
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
          <Box sx={{ mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 0.5,
              }}
            >
              <Typography variant="caption">Data Entry</Typography>
              <Typography variant="caption">
                {Math.round(dataProgress)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={dataProgress}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme.palette.contentPipelineColors.planning,
                },
              }}
            />
          </Box>
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 0.5,
              }}
            >
              <Typography variant="caption">Compliance</Typography>
              <Typography variant="caption">
                {totalFields > 0
                  ? `${approvedFields}/${totalFields} fields approved`
                  : `${Math.round(complianceProgress)}%`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={complianceProgress}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor:
                    theme.palette.contentPipelineColors.compliance,
                },
              }}
            />
          </Box>
        </Box>
      </Card>
    </Box>
  )
}

export default function CourseListWithProgress({
  courses,
  coursesProgress,
  activeCourseID,
  onSelectCourse,
  isLoading,
  filterType = 'all',
  searchQuery = '',
}: CourseListWithProgressProps): ReactElement {
  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        {[...Array(3)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            sx={{
              height: 140,
              mb: 1,
              borderRadius: 1,
            }}
          />
        ))}
      </Box>
    )
  }

  if (courses.length === 0) {
    let emptyMessage =
      filterType === 'needs-revision'
        ? 'Great! No courses currently need revision.'
        : filterType === 'in-planning'
          ? 'No courses are currently in planning.'
          : filterType === 'ready-for-review'
            ? 'No courses are ready for compliance review.'
            : filterType === 'approved'
              ? 'No courses have been fully approved yet.'
              : searchQuery
                ? `No courses match "${searchQuery}".`
                : 'No courses available.'

    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'background.paperDark',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          No Courses Found
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {emptyMessage}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      role="list"
      aria-label="Planning courses"
    >
      {courses.map((course) => {
        let checked = activeCourseID === course.id
        let progress = coursesProgress[course.id]

        return (
          <CourseCard
            key={course.id}
            course={course}
            progress={progress}
            checked={checked}
            onSelect={() => onSelectCourse(course.id)}
          />
        )
      })}
    </Box>
  )
}
